import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addAttendance, removeAttendance, checkOutAttendance, markAttendancePrinted } from '@/data/events';
import { DateTime } from 'luxon';

export default function useCheckinActions({ eventInstance, setAvailablePeople, checkinLocation, loggedInUser, selectedFilter }) {
    const queryClient = useQueryClient();

    // --- Optimistic list updaters ---

    const updatePerson = useCallback((prevState, attendeeID, updateFn) => {
        return prevState.map(p => {
            let changed = false;
            let updated = p;

            if (p.id === attendeeID) {
                updated = updateFn(p);
                changed = true;
            }

            if (p?.Family?.FamilyMembers?.some(m => m.id === attendeeID) || p?.Address?.People?.some(a => a.id === attendeeID)) {
                // For nested family/address members, only update attendance-related fields
                // to avoid unnecessary rerenders of unrelated cards
                const updateNested = (member) => {
                    if (member.id !== attendeeID) return member;
                    const updated = updateFn(member);
                    // Only keep attendance-related fields for nested members
                    return {
                        ...member,
                        isSelected: updated.isSelected,
                        isCheckedOut: updated.isCheckedOut,
                        isPending: updated.isPending,
                        isProcessing: updated.isProcessing,
                        selectionDetail: updated.selectionDetail,
                        checkInLocation: updated.checkInLocation,
                        checkInTime: updated.checkInTime,
                        submitterName: updated.submitterName,
                    };
                };
                updated = {
                    ...(changed ? updated : p),
                    Address: p.Address ? { ...p.Address, People: p.Address.People?.map(updateNested) } : p.Address,
                    Family: p.Family ? { ...p.Family, FamilyMembers: p.Family.FamilyMembers?.map(updateNested) } : p.Family,
                };
                changed = true;
            }

            return changed ? updated : p;
        });
    }, []);

    const addListAttendance = useCallback((data, failed = false) => {
        if (!failed) {
            queryClient.setQueryData(['attendance', eventInstance?.id], old => {
                return [...(old?.filter(item => item?.attendeeID !== data?.attendeeID) || []), data];
            });
        }

        setAvailablePeople(prev => {
            // On "Not Checked In" filter, a successful check-in means the person no
            // longer belongs in the list — remove them immediately (optimistic).
            const base = (!failed && selectedFilter === "Not Checked In")
                ? prev.filter(p => p.id !== data?.attendeeID)
                : prev;

            // The attendee may exist only as a nested family/address member on
            // another person's card rather than a top-level row (filtered lists,
            // unloaded pages). Always run updatePerson so nested copies still get
            // isProcessing cleared and selection state applied.
            return updatePerson(base, data?.attendeeID, (person) => ({
                ...person,
                isSelected: !failed,
                isCheckedOut: false,
                checkedOutAt: null,
                isProcessing: false,
                isPending: false,
                subtitle: !failed
                    ? (() => {
                        const locationLabel = data?.Location?.name || (eventInstance?.Locations?.length > 0 ? "No Location Specified" : "");
                        return (locationLabel ? locationLabel + " • " : "") + DateTime.fromISO(data?.createdAt).toLocaleString(DateTime.DATETIME_MED);
                    })()
                    : person.subtitle,
                selectionDetail: !failed ? (data?.Location?.name || (eventInstance?.Locations?.length > 0 ? "No Location Specified" : "")) : "",
                checkInLocation: !failed ? (data?.Location?.name || null) : person.checkInLocation,
                checkInTime: !failed ? (data?.createdAt || null) : person.checkInTime,
                submitterName: !failed && data?.Submitter ? `${data.Submitter.firstName} ${data.Submitter.lastName}` : person.submitterName,
            }));
        });
    }, [eventInstance?.id, queryClient, setAvailablePeople, updatePerson, selectedFilter]);

    const removeListAttendance = useCallback((data, failed = false) => {
        if (!failed) {
            queryClient.setQueryData(['attendance', eventInstance?.id], old => {
                return old?.filter(item => item?.attendeeID !== data?.attendeeID) || [];
            });
        }

        setAvailablePeople(prev => {
            // On "Checked In" filter, a successful check-in removal means the person
            // no longer belongs in the list — remove them immediately (optimistic).
            if (!failed && selectedFilter === "Checked In") {
                return prev.filter(p => p.id !== data?.attendeeID);
            }

            return updatePerson(prev, data?.attendeeID, (person) => ({
                ...person,
                isSelected: !!failed,
                isCheckedOut: false,
                checkedOutAt: null,
                isProcessing: false,
                isPending: false,
                subtitle: failed ? person.subtitle : "",
                selectionDetail: "",
            }));
        });
    }, [eventInstance?.id, queryClient, setAvailablePeople, updatePerson, selectedFilter]);

    const checkOutListAttendance = useCallback((data, failed = false) => {
        if (!failed) {
            queryClient.setQueryData(['attendance', eventInstance?.id], old => {
                return old?.map(item => item?.attendeeID === data?.attendeeID ? { ...item, checkedOutAt: data?.checkedOutAt } : item);
            });
        }

        const checkedOutTime = failed ? null : DateTime.fromISO(data?.checkedOutAt).toLocaleString(DateTime.TIME_SIMPLE);

        setAvailablePeople(prev => updatePerson(prev, data?.attendeeID, (person) => ({
            ...person,
            isProcessing: false,
            isCheckedOut: !failed,
            checkedOutAt: failed ? null : data?.checkedOutAt,
            subtitle: failed ? person.subtitle : `Checked out at ${checkedOutTime}`,
            selectionDetail: !failed ? "Checked out" : person.selectionDetail,
        })));
    }, [eventInstance?.id, queryClient, setAvailablePeople, updatePerson]);

    const updateListAttendance = useCallback((data) => {
        // Update from realtime broadcast (e.g., labelPrinted changes from another device)
        setAvailablePeople(prev => updatePerson(prev, data?.attendeeID, (person) => ({
            ...person,
            labelPrinted: data?.labelPrinted ?? person.labelPrinted,
            checkInLocation: data?.Location?.name || person.checkInLocation,
            checkInTime: data?.createdAt || person.checkInTime,
            submitterName: data?.Submitter ? `${data.Submitter.firstName} ${data.Submitter.lastName}` : person.submitterName,
        })));
    }, [setAvailablePeople, updatePerson]);

    // --- Mutations ---

    const addAttendanceMutation = useMutation({
        mutationFn: addAttendance,
        onMutate: (context) => context,
        onSuccess: (data, vars) => {
            if (data?.attendeeID) {
                addListAttendance(data, false);
            } else if (vars?.personID) {
                // upsert with ignoreDuplicates returns no row when the record
                // already exists — clear the pending state; realtime/refresh
                // will supply the full record.
                setAvailablePeople(prev => updatePerson(prev, vars.personID, (p) => ({ ...p, isProcessing: false })));
            }
        },
        onError: (error, vars, context) => { addListAttendance({ ...context, attendeeID: context?.personID }, true); },
    });

    const removeAttendanceMutation = useMutation({
        mutationFn: removeAttendance,
        onMutate: (context) => context,
        onSuccess: (data) => { if (data?.attendeeID) removeListAttendance(data, false); },
        onError: (error, vars, context) => { removeListAttendance({ ...context, attendeeID: context?.personID }, true); },
    });

    const checkOutAttendanceMutation = useMutation({
        mutationFn: checkOutAttendance,
        onMutate: (context) => context,
        onSuccess: (data) => { if (data?.attendeeID) checkOutListAttendance(data, false); },
        onError: (error, vars, context) => { checkOutListAttendance({ ...context, attendeeID: context?.attendeeID }, true); },
    });

    // --- Check In orchestrator ---

    const checkIn = useCallback((profile, isSelected, _isCheckedOut = false, mainFamilyMember = null) => {
        // For "Not Checked In" filter, skip isProcessing to avoid a two-step
        // re-render (processing → removed) that causes LegendList to lose its
        // scroll position. The item will be removed in addListAttendance onSuccess.
        if (selectedFilter !== "Not Checked In" || isSelected) {
            setAvailablePeople(prev => updatePerson(prev, profile.id, (p) => ({ ...p, isProcessing: true })));
        }

        if (isSelected) {
            removeAttendanceMutation.mutate({ attendeeID: profile?.id, eventInstanceID: eventInstance?.id, mainFamilyMember });
        } else {
            const locationID = checkinLocation?.id || eventInstance?.Locations?.[0]?.id;
            addAttendanceMutation.mutate({
                eventInstanceID: eventInstance?.id,
                personID: profile?.id,
                submitterID: loggedInUser?.id,
                locationID,
                mainFamilyMember,
            });
        }
    }, [checkinLocation, eventInstance?.id, eventInstance?.Locations, loggedInUser?.id, selectedFilter]);

    const checkOut = useCallback((profile, mainFamilyMember = null) => {
        setAvailablePeople(prev => updatePerson(prev, profile.id, (p) => ({ ...p, isProcessing: true })));
        checkOutAttendanceMutation.mutate({ attendeeID: profile?.id, eventInstanceID: eventInstance?.id, mainFamilyMember });
    }, [eventInstance?.id]);

    // --- Mark as Printed ---

    const markAttendancePrintedMutation = useMutation({
        mutationFn: markAttendancePrinted,
        onMutate: (context) => context,
        onSuccess: (data, vars) => {
            // Update attendance query cache to mark as printed
            queryClient.setQueryData(['attendance', eventInstance?.id], old => {
                return old?.map(item => {
                    if (!vars.attendeeIDs.includes(item.attendeeID)) return item;
                    const updated = { ...item, labelPrinted: true };
                    if (vars.labelPrintCode !== undefined) {
                        updated.labelPrintCode = vars.labelPrintCode;
                    }
                    return updated;
                });
            });
            // Update availablePeople to reflect printed state
            setAvailablePeople(prev => prev.map(p => {
                if (!vars.attendeeIDs.includes(p.id)) return p;
                const updated = { ...p, labelPrinted: true };
                if (vars.labelPrintCode !== undefined) {
                    updated.labelPrintCode = vars.labelPrintCode;
                }
                return updated;
            }));
        },
    });

    // --- Label Queue ---

    const addPersonToLabelQueue = useCallback((profile, setLabelQueue) => {
        setLabelQueue(prev => [...prev, profile]);
        setAvailablePeople(prev => prev.map(p => {
            let changed = false;
            let updated = p;

            if (p.id === profile.id) {
                updated = { ...p, isPending: true };
                changed = true;
            }

            if (p?.Family?.FamilyMembers?.some(m => m.id === profile.id) || p?.Address?.People?.some(m => m.id === profile.id)) {
                updated = {
                    ...(changed ? updated : p),
                    Address: p.Address ? { ...p.Address, People: p.Address.People?.map(a => a.id === profile.id ? { ...a, isPending: true } : a) } : p.Address,
                    Family: p.Family ? { ...p.Family, FamilyMembers: p.Family.FamilyMembers?.map(m => m.id === profile.id ? { ...m, isPending: true } : m) } : p.Family,
                };
                changed = true;
            }

            return changed ? updated : p;
        }));
    }, [setAvailablePeople]);

    const removePersonFromLabelQueue = useCallback((profile, setLabelQueue) => {
        setLabelQueue(prev => prev.filter(p => p.id !== profile.id));
        setAvailablePeople(prev => prev.map(p => {
            let changed = false;
            let updated = p;

            if (p.id === profile.id) {
                updated = { ...p, isPending: false };
                changed = true;
            }

            if (p?.Family?.FamilyMembers?.some(m => m.id === profile.id) || p?.Address?.People?.some(m => m.id === profile.id)) {
                updated = {
                    ...(changed ? updated : p),
                    Address: p.Address ? { ...p.Address, People: p.Address.People?.map(a => a.id === profile.id ? { ...a, isPending: false } : a) } : p.Address,
                    Family: p.Family ? { ...p.Family, FamilyMembers: p.Family.FamilyMembers?.map(m => m.id === profile.id ? { ...m, isPending: false } : m) } : p.Family,
                };
                changed = true;
            }

            return changed ? updated : p;
        }));
    }, [setAvailablePeople]);

    const clearAllPending = useCallback((setLabelQueue) => {
        setLabelQueue([]);
        setAvailablePeople(prev => prev.map(p => {
            const hasPending = p.isPending || p?.Family?.FamilyMembers?.some(m => m.isPending) || p?.Address?.People?.some(a => a.isPending);
            if (!hasPending) return p;
            return {
                ...p,
                isPending: false,
                Address: p.Address ? { ...p.Address, People: p.Address.People?.map(a => a.isPending ? { ...a, isPending: false } : a) } : p.Address,
                Family: p.Family ? { ...p.Family, FamilyMembers: p.Family.FamilyMembers?.map(m => m.isPending ? { ...m, isPending: false } : m) } : p.Family,
            };
        }));
    }, [setAvailablePeople]);

    return {
        checkIn,
        checkOut,
        addListAttendance,
        removeListAttendance,
        checkOutListAttendance,
        updateListAttendance,
        addAttendanceMutation,
        removeAttendanceMutation,
        markAttendancePrintedMutation,
        addPersonToLabelQueue,
        removePersonFromLabelQueue,
        clearAllPending,
    };
}
